// Helpers to render communication LogEvents in a user-friendly way.
// They only affect visualization in the frontend: no database or API change.
// All functions are pure and receive `t` (i18next translate) from the caller.

const ENTITY_ALIASES = ["comment", "file", "answer"];

const UNBLOCK_KEYWORDS = [
  "desbloq",
  "desbl o",
  "unblock",
  "débloq",
  "debloq",
  "sblocc",
  "desbl",
  "ontgrendel",
];

const BLOCK_KEYWORDS = ["bloq", "block", "blocc", "vergrendel"];

export const normalizeEntityType = (log) => {
  const raw = (log?.entityType || "").toString().trim().toLowerCase();
  if (ENTITY_ALIASES.includes(raw)) {
    return raw;
  }
  // Fallback for historic rows without entityType: try to infer it from the
  // translated message ("tipo Comment", "type Comment", ...). Language agnostic
  // enough for the shapes stored by the API.
  const message = (log?.message || "").toString().toLowerCase();
  const match = message.match(
    /(?:tipo|type|tipus|tipo de|tipu)\s+(comment|file|answer)/i,
  );
  if (match && ENTITY_ALIASES.includes(match[1].toLowerCase())) {
    return match[1].toLowerCase();
  }
  return raw || "";
};

export const isUnblockedMessage = (message) => {
  const text = (message || "").toString().toLowerCase();
  return UNBLOCK_KEYWORDS.some((keyword) => text.includes(keyword));
};

export const isBlockedMessage = (message) => {
  const text = (message || "").toString().toLowerCase();
  return BLOCK_KEYWORDS.some((keyword) => text.includes(keyword));
};

export const getLogEventUserDisplayName = (log, t) => {
  const firstName = (log?.user?.name || "").toString().trim();
  const lastName = (log?.user?.lastName || "").toString().trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }
  if (typeof t === "function") {
    return t("edit.log.systemUser", "Sistema");
  }
  return "Sistema";
};

// Returns the i18n key used for the "Evento" column.
export const getLogEventDisplayKey = (log) => {
  if (!log?.eventName) {
    return "";
  }

  if (log.eventName === "email.sent") {
    if (log.emailTemplateType) {
      return `edit.log.events.email.${log.emailTemplateType}`;
    }
    return "edit.log.events.email.sent";
  }

  if (
    log.eventName === "entity.created" ||
    log.eventName === "entity.updated" ||
    log.eventName === "entity.deleted"
  ) {
    const action =
      log.eventName === "entity.created"
        ? "created"
        : log.eventName === "entity.updated"
          ? "updated"
          : "deleted";
    const entity = normalizeEntityType(log);
    if (ENTITY_ALIASES.includes(entity)) {
      return `edit.log.events.entity.${entity}.${action}`;
    }
    return `edit.log.events.entity.${action}`;
  }

  if (log.eventName === "communication.block_status_changed") {
    if (isUnblockedMessage(log?.message)) {
      return "edit.log.events.communication.unblocked";
    }
    if (isBlockedMessage(log?.message)) {
      return "edit.log.events.communication.blocked";
    }
    return "edit.log.events.communication.block_status_changed";
  }

  return `edit.log.events.${log.eventName}`;
};

// Translated label for the "Evento" column.
export const getLogEventDisplay = (t, log) => {
  const key = getLogEventDisplayKey(log);
  if (!key) {
    return "";
  }
  return t(key, log?.eventName || key);
};

// Friendly text for the "Mensaje" column. For entity.* events it is rebuilt
// from structured fields (user + entity type) so it never leaks technical
// sentences such as "entidad de tipo Comment con ID 45". For every other
// event the API message is kept untouched.
export const getLogEventDisplayMessage = (t, log) => {
  const original = log?.message || "";
  if (
    log?.eventName === "entity.created" ||
    log?.eventName === "entity.updated" ||
    log?.eventName === "entity.deleted"
  ) {
    const action =
      log.eventName === "entity.created"
        ? "created"
        : log.eventName === "entity.updated"
          ? "updated"
          : "deleted";
    const entity = normalizeEntityType(log);
    const user = getLogEventUserDisplayName(log, t);
    if (ENTITY_ALIASES.includes(entity)) {
      return t(`edit.log.messages.entity.${entity}.${action}`, original, {
        user,
      });
    }
    return t("edit.log.messages.entity.generic." + action, original, {
      user,
      entity: log?.entityType || entity || "",
    });
  }
  return original;
};

// Translated text for the "Resultado" column. Raw values arrive in English
// ("success", "failure: ...", "skipped: ...").
export const getLogEventDisplayResult = (t, log) => {
  const result = (log?.result || "").toString();
  if (!result.trim()) {
    return "";
  }
  const lowered = result.toLowerCase();
  if (lowered.startsWith("skipped")) {
    return t("edit.log.results.skipped", result);
  }
  if (lowered.startsWith("failure") || lowered.startsWith("error")) {
    return t("edit.log.results.error", result);
  }
  if (lowered === "success") {
    return t("edit.log.results.success", result);
  }
  return result;
};
